import { OverlayRef } from '@angular/cdk/overlay';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BbitArray } from '@bbitgmbh/bbit.utils';

export enum BbitImageOverlayItemType {
  IMAGE = 'image',
  VIDEO = 'video',
}
export interface IBbitImageOverlayParams {
  items: BbitImageOverlayItem[];
  currentIndex?: number;
}

export type BbitImageOverlayItem = IBbitImageOverlayImageItem | IBbitImageOverlayVideoItem;

export interface IBbitImageOverlayBaseItem {
  type: BbitImageOverlayItemType;
  alt?: string;
}

export interface IBbitImageOverlayImageItem extends IBbitImageOverlayBaseItem {
  type: BbitImageOverlayItemType.IMAGE;
  url: string;
}

export interface IBbitImageOverlayVideoItem extends IBbitImageOverlayBaseItem {
  type: BbitImageOverlayItemType.VIDEO;
  youTubeId?: string;
  vimeoId?: string;
}

export interface IBbitImageOverlayImage {
  file?: { url?: string };
  alt?: string;
}

export interface IBbitImageOverlayVideo {
  youTubeId?: string;
  vimeoId: string;
}

export interface IBbitImageOverlayUrlItem {
  type: BbitImageOverlayItemType;
  url: string;
  sanitizedUrl?: SafeResourceUrl;
  thumbnail: string;
}

export const convertImageOverlayItems = async (
  items: BbitImageOverlayItem[],
  sanitizer: DomSanitizer,
): Promise<IBbitImageOverlayUrlItem[]> => {
  const urls: IBbitImageOverlayUrlItem[] = (
    await Promise.all(
      items.map(async (o): Promise<IBbitImageOverlayUrlItem | undefined> => {
        switch (o.type) {
          case BbitImageOverlayItemType.IMAGE:
            return { type: BbitImageOverlayItemType.IMAGE, url: o.url, thumbnail: o.url };
          case BbitImageOverlayItemType.VIDEO:
            switch (true) {
              case !!o.youTubeId: {
                const url = `https://www.youtube.com/embed/${o.youTubeId}?rel=0`;
                return {
                  type: BbitImageOverlayItemType.VIDEO,
                  url,
                  sanitizedUrl: sanitizer.bypassSecurityTrustResourceUrl(url),
                  thumbnail: `https://img.youtube.com/vi/${o.youTubeId}/maxresdefault.jpg`,
                };
              }
              case !!o.vimeoId: {
                let thumbnail = '/assets/images/placeholder-image.png';
                try {
                  const embedUrl = `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${o.vimeoId}`;
                  const embedResult = await fetch(embedUrl);
                  const embedData = await embedResult.json();
                  thumbnail = embedData?.thumbnail_url || thumbnail;
                } catch {
                  // do nothing if this fails
                }
                const url = `https://player.vimeo.com/video/${o.vimeoId}`;
                return {
                  type: BbitImageOverlayItemType.VIDEO,
                  url,
                  sanitizedUrl: sanitizer.bypassSecurityTrustResourceUrl(url),
                  thumbnail,
                };
              }
            }
        }
        return undefined;
      }),
    )
  ).filter((item): item is IBbitImageOverlayUrlItem => !!item);

  if (urls.length === 0) {
    const placeholder = '/assets/images/placeholder-image.png';
    urls.push({ type: BbitImageOverlayItemType.IMAGE, url: placeholder, thumbnail: placeholder });
  }
  return BbitArray.uniqueBy(urls, (e) => e.url);
};

export class ImageOverlayRef {
  constructor(
    private _overlayRef: OverlayRef,
    private _params: IBbitImageOverlayParams,
  ) {}
  getItems(): BbitImageOverlayItem[] {
    return this._params?.items || [];
  }
  getCurrentIndex: () => number = () => this._params?.currentIndex || 0;
  close() {
    this._overlayRef.dispose();
  }
}
